import { useMemo, useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function ProDatePicker({
  field,
  form,
  label,
  icon,
  placeholder,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const hasError = form.touched?.[field.name] && form.errors?.[field.name];

  const today = useMemo(() => {
    const t = new Date();
    return new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate()));
  }, []);

  const selectedDate = useMemo(() => {
    if (!field.value) return null;
    const [y, m, d] = String(field.value).split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(Date.UTC(y, m - 1, d));
  }, [field.value]);

  const [viewYear, setViewYear] = useState(
    selectedDate?.getUTCFullYear() || today.getUTCFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    selectedDate?.getUTCMonth() || today.getUTCMonth()
  );

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    const base = selectedDate || today;
    setViewYear(base.getUTCFullYear());
    setViewMonth(base.getUTCMonth());
  }, [selectedDate, today]);

  const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate();
  const firstDayOfMonth = new Date(Date.UTC(viewYear, viewMonth, 1)).getUTCDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const isSameDay = (a, b) =>
    !!a &&
    !!b &&
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate();

  const selectDate = (day) => {
    const d = new Date(Date.UTC(viewYear, viewMonth, day));
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    form.setFieldValue(field.name, `${yyyy}-${mm}-${dd}`);
    setOpen(false);
  };

  const changeMonth = (direction) => {
    const next = new Date(Date.UTC(viewYear, viewMonth + direction, 1));
    setViewYear(next.getUTCFullYear());
    setViewMonth(next.getUTCMonth());
  };

  const formattedValue = selectedDate
    ? `${String(selectedDate.getUTCDate()).padStart(2, "0")}/${String(
        selectedDate.getUTCMonth() + 1
      ).padStart(2, "0")}/${selectedDate.getUTCFullYear()}`
    : "";

  const placeholderText = placeholder || `Select ${label?.toLowerCase() || "date"}`;

  return (
    <div className="relative mb-6" ref={containerRef}>
      {label && (
        <label
          htmlFor={field.name}
          className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {label}
        </label>
      )}

      <button
        type="button"
        id={field.name}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          if (!disabled) setOpen((v) => !v);
        }}
        className={`group relative w-full overflow-hidden rounded-2xl border bg-white px-4 py-3 text-left transition-all dark:bg-slate-900 ${
          hasError
            ? "border-red-400 ring-2 ring-red-100 dark:border-red-500/70 dark:ring-red-500/10"
            : "border-slate-300 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500"
        } ${
          disabled
            ? "cursor-not-allowed bg-slate-100 opacity-70 dark:bg-slate-800/70"
            : "cursor-pointer focus:outline-none focus-visible:border-slate-900 focus-visible:ring-4 focus-visible:ring-slate-200 dark:focus-visible:border-white dark:focus-visible:ring-slate-800"
        }`}
      >
        {icon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-slate-400 dark:text-slate-500">
            {icon}
          </span>
        )}

        <div
          className={`pr-8 text-sm font-medium ${
            icon ? "pl-6" : ""
          } ${formattedValue ? "text-slate-900 dark:text-white" : "text-slate-400 dark:text-slate-500"}`}
        >
          {formattedValue || placeholderText}
        </div>

        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition dark:text-slate-500">
          📅
        </span>
      </button>

      {hasError && (
        <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
          {form.errors[field.name]}
        </p>
      )}

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute z-50 mt-2 w-[320px] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
          >
            {/* Header */}
            <div className="mb-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label="Previous month"
              >
                ‹
              </button>

              <div className="text-sm font-semibold text-slate-900 dark:text-white">
                {new Date(Date.UTC(viewYear, viewMonth, 1)).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                  timeZone: "UTC",
                })}
              </div>

              <button
                type="button"
                onClick={() => changeMonth(1)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label="Next month"
              >
                ›
              </button>
            </div>

            {/* Weekdays */}
            <div className="mb-2 grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                <div key={d} className="py-1">
                  {d}
                </div>
              ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7 gap-1.5 text-center">
              {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}

              {days.map((day) => {
                const d = new Date(Date.UTC(viewYear, viewMonth, day));
                const isToday = isSameDay(d, today);
                const isSelected = isSameDay(d, selectedDate);

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => selectDate(day)}
                    className={`flex h-10 w-10 items-center justify-center rounded-xl text-sm font-medium transition ${
                      isSelected
                        ? "bg-black text-white dark:bg-white dark:text-black"
                        : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    } ${
                      isToday && !isSelected
                        ? "ring-1 ring-slate-300 dark:ring-slate-600"
                        : ""
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Footer helper */}
            <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-xs dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  const yyyy = today.getUTCFullYear();
                  const mm = String(today.getUTCMonth() + 1).padStart(2, "0");
                  const dd = String(today.getUTCDate()).padStart(2, "0");
                  form.setFieldValue(field.name, `${yyyy}-${mm}-${dd}`);
                  setOpen(false);
                }}
                className="font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
              >
                Today
              </button>

              {field.value && (
                <button
                  type="button"
                  onClick={() => {
                    form.setFieldValue(field.name, "");
                    setOpen(false);
                  }}
                  className="font-medium text-slate-500 transition hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
                >
                  Clear
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}